import { useState } from 'react';
import { 
  TrendingUp, 
  ShoppingCart, 
  DollarSign, 
  Users, 
  Clock, 
  AlertTriangle,
  BarChart3,
  PieChart,
  ArrowUpRight,
  ArrowDownRight,
  Package,
  Star,
  Flame
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { motion } from 'framer-motion';

// Simulated dashboard data - impressive metrics for demo
const dashboardData = {
  today: {
    sales: 45780,
    salesGrowth: 18.5,
    orders: 156,
    ordersGrowth: 12.3,
    avgTicket: 293,
    avgTicketGrowth: 5.6,
    newCustomers: 34,
  },
  week: {
    sales: 312450,
    salesGrowth: 22.1,
    orders: 1089,
    ordersGrowth: 15.8,
    avgTicket: 287,
    avgTicketGrowth: 5.3,
    newCustomers: 245,
  },
  topProducts: [
    { name: 'Bowl Mediterráneo', orders: 89, revenue: 16821, growth: 24 },
    { name: 'Poke Bowl Salmón', orders: 76, revenue: 17100, growth: 18 },
    { name: 'Tacos de Carnitas', orders: 72, revenue: 11880, growth: 31 },
    { name: 'Pollo al Limón', orders: 64, revenue: 13760, growth: 12 },
    { name: 'Combo Para Dos', orders: 58, revenue: 30160, growth: 45 },
  ],
  peakHours: [
    { hour: '12:00', orders: 28 },
    { hour: '13:00', orders: 42 },
    { hour: '14:00', orders: 35 },
    { hour: '19:00', orders: 31 },
    { hour: '20:00', orders: 48 },
    { hour: '21:00', orders: 38 },
  ],
  abandonment: {
    rate: 12.4,
    previousRate: 15.2,
    topReasons: [
      { reason: 'Precio de envío', percentage: 35 },
      { reason: 'Tiempo de entrega largo', percentage: 28 },
      { reason: 'Producto agotado', percentage: 22 },
      { reason: 'Proceso complejo', percentage: 15 },
    ],
  },
  complaints: [
    { issue: 'Comida fría', count: 8, trend: 'down' },
    { issue: 'Pedido incompleto', count: 5, trend: 'down' },
    { issue: 'Entrega tardía', count: 12, trend: 'up' },
    { issue: 'Falta de salsa/extras', count: 3, trend: 'down' },
  ],
  satisfaction: {
    rating: 4.8,
    totalReviews: 2547,
    distribution: [
      { stars: 5, count: 1890 },
      { stars: 4, count: 480 },
      { stars: 3, count: 120 },
      { stars: 2, count: 35 },
      { stars: 1, count: 22 },
    ],
  },
};

function MetricCard({ title, value, growth, prefix = '', suffix = '', icon: Icon }) {
  const isPositive = growth >= 0;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-card border border-border/50 rounded-2xl p-6"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="text-3xl font-semibold mt-2">
            {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
          </p>
        </div>
        <div className="w-12 h-12 bg-accent/10 rounded-xl flex items-center justify-center">
          <Icon className="w-6 h-6 text-accent" />
        </div>
      </div>
      {growth !== undefined && (
        <div className={`flex items-center gap-1 mt-3 text-sm ${
          isPositive ? 'text-green-600' : 'text-red-500'
        }`}>
          {isPositive ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
          <span>{Math.abs(growth)}%</span>
          <span className="text-muted-foreground">vs periodo anterior</span>
        </div>
      )}
    </motion.div>
  );
}

export default function PanelPage() {
  const [period, setPeriod] = useState('today');
  const data = period === 'today' ? dashboardData.today : dashboardData.week;

  return (
    <div className="min-h-screen pb-24 md:pb-12 bg-secondary/20">
      {/* Header */}
      <div className="bg-primary text-primary-foreground py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-heading text-2xl md:text-3xl font-medium">Panel de Control</h1>
                <Badge className="bg-white/20 text-white">Demo</Badge>
              </div>
              <p className="text-primary-foreground/70 mt-1">
                Vista general del rendimiento de tu negocio
              </p>
            </div>
            <Tabs value={period} onValueChange={setPeriod} className="w-fit">
              <TabsList className="bg-white/10">
                <TabsTrigger value="today" className="data-[state=active]:bg-white data-[state=active]:text-primary">
                  Hoy
                </TabsTrigger>
                <TabsTrigger value="week" className="data-[state=active]:bg-white data-[state=active]:text-primary">
                  Esta semana
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Demo notice */}
        <div className="mb-6 p-4 bg-accent/10 border border-accent/30 rounded-xl flex items-center gap-3">
          <BarChart3 className="w-5 h-5 text-accent" />
          <p className="text-sm">
            <strong>Modo demo:</strong> Los datos mostrados son simulados para demostrar las capacidades del panel.
          </p>
        </div>

        {/* Main Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <MetricCard
            title="Ventas"
            value={data.sales}
            prefix="$"
            growth={data.salesGrowth}
            icon={DollarSign}
          />
          <MetricCard
            title="Pedidos"
            value={data.orders}
            growth={data.ordersGrowth}
            icon={ShoppingCart}
          />
          <MetricCard
            title="Ticket promedio"
            value={data.avgTicket}
            prefix="$"
            growth={data.avgTicketGrowth}
            icon={TrendingUp}
          />
          <MetricCard
            title="Clientes nuevos"
            value={data.newCustomers}
            icon={Users}
          />
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          {/* Top Products */}
          <div className="bg-card border border-border/50 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-medium flex items-center gap-2">
                <Flame className="w-5 h-5 text-accent" />
                Productos más vendidos
              </h2>
            </div>
            <div className="space-y-4">
              {dashboardData.topProducts.map((product, index) => (
                <div key={product.name} className="flex items-center gap-4">
                  <span className="w-6 h-6 bg-secondary rounded-full flex items-center justify-center text-sm font-medium">
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{product.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {product.orders} pedidos · ${product.revenue.toLocaleString()}
                    </p>
                  </div>
                  <Badge className={`${product.growth > 20 ? 'bg-green-100 text-green-800' : 'bg-secondary'}`}>
                    +{product.growth}%
                  </Badge>
                </div>
              ))}
            </div>
          </div>

          {/* Peak Hours */}
          <div className="bg-card border border-border/50 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-medium flex items-center gap-2">
                <Clock className="w-5 h-5 text-accent" />
                Horas pico
              </h2>
            </div>
            <div className="space-y-3">
              {dashboardData.peakHours.map((hour) => {
                const maxOrders = Math.max(...dashboardData.peakHours.map(h => h.orders));
                const percentage = (hour.orders / maxOrders) * 100;
                
                return (
                  <div key={hour.hour} className="flex items-center gap-4">
                    <span className="w-14 text-sm text-muted-foreground">{hour.hour}</span>
                    <div className="flex-1 h-8 bg-secondary rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${percentage}%` }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="h-full bg-accent rounded-full flex items-center justify-end pr-3"
                      >
                        <span className="text-xs font-medium text-accent-foreground">
                          {hour.orders}
                        </span>
                      </motion.div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          {/* Cart Abandonment */}
          <div className="bg-card border border-border/50 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-medium flex items-center gap-2">
                <Package className="w-5 h-5 text-accent" />
                Abandono de carrito
              </h2>
            </div>
            <div className="text-center py-4">
              <p className="text-4xl font-semibold">{dashboardData.abandonment.rate}%</p>
              <p className="text-sm text-green-600 flex items-center justify-center gap-1 mt-1">
                <ArrowDownRight className="w-4 h-4" />
                Mejoró {(dashboardData.abandonment.previousRate - dashboardData.abandonment.rate).toFixed(1)}%
              </p>
            </div>
            <div className="mt-4 space-y-2">
              {dashboardData.abandonment.topReasons.map(reason => (
                <div key={reason.reason} className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{reason.reason}</span>
                  <span className="font-medium">{reason.percentage}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Complaints */}
          <div className="bg-card border border-border/50 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-medium flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Motivos de queja
              </h2>
            </div>
            <div className="space-y-3">
              {dashboardData.complaints.map(complaint => (
                <div key={complaint.issue} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${
                      complaint.trend === 'down' ? 'bg-green-500' : 'bg-red-500'
                    }`} />
                    <span className="text-sm">{complaint.issue}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">{complaint.count}</span>
                    {complaint.trend === 'down' ? (
                      <ArrowDownRight className="w-4 h-4 text-green-500" />
                    ) : (
                      <ArrowUpRight className="w-4 h-4 text-red-500" />
                    )}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-muted-foreground mt-4 pt-4 border-t border-border">
              Los indicadores muestran tendencia vs semana anterior
            </p>
          </div>

          {/* Customer Satisfaction */}
          <div className="bg-card border border-border/50 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-medium flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-500" />
                Satisfacción
              </h2>
            </div>
            <div className="text-center py-4">
              <div className="flex items-center justify-center gap-1">
                {[1, 2, 3, 4, 5].map(star => (
                  <Star 
                    key={star} 
                    className={`w-6 h-6 ${
                      star <= Math.round(dashboardData.satisfaction.rating) 
                        ? 'text-yellow-500 fill-yellow-500' 
                        : 'text-muted'
                    }`} 
                  />
                ))}
              </div>
              <p className="text-3xl font-semibold mt-2">{dashboardData.satisfaction.rating}</p>
              <p className="text-sm text-muted-foreground">
                {dashboardData.satisfaction.totalReviews.toLocaleString()} reseñas
              </p>
            </div>
            <div className="mt-4 space-y-2">
              {dashboardData.satisfaction.distribution.map(dist => {
                const percentage = (dist.count / dashboardData.satisfaction.totalReviews) * 100;
                return (
                  <div key={dist.stars} className="flex items-center gap-2 text-sm">
                    <span className="w-4">{dist.stars}</span>
                    <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                    <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-yellow-500 rounded-full"
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    <span className="w-12 text-right text-muted-foreground">
                      {percentage.toFixed(0)}%
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
